import { mergePopularidadEncabezado } from './popularidadReportModel';

export const DESCUENTOS_REPORT_TITULO_DEFAULT = 'RELACIÓN DE DESCUENTOS Y CORTESÍAS';

function foldKeyLoose(k) {
  return String(k)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[_\s-]/g, '')
    .toLowerCase();
}

function keyMatches(spec, rawKey) {
  return spec.patterns.some((re) => re.test(rawKey) || re.test(foldKeyLoose(rawKey)));
}

/**
 * Orden y etiquetas del impreso; las claves del API se resuelven contra la primera fila.
 * @type {ReadonlyArray<{ id: string, label: string, patterns: RegExp[], align: 'left' | 'right', total?: boolean, kind?: 'date' | 'money' | 'percent' | 'text' }>}
 */
export const DESCUENTOS_COLUMN_SPECS = [
  {
    id: 'fecha',
    label: 'Fecha',
    patterns: [
      /^fecha$/i,
      /^fechahora$/i,
      /^fechaoperacion$/i,
      /^fechaOperacion$/i,
      /^fechamovimiento$/i,
      /^fechamov$/i,
      /^fecha_movimiento$/i,
      /^fecharegistro$/i,
      /^fechaticket$/i,
      /^fechacuenta$/i,
      /^fecventa$/i,
      /^date$/i,
    ],
    align: 'left',
    kind: 'date',
  },
  {
    id: 'caja',
    label: 'Caja',
    patterns: [/^caja$/i, /^nombrecaja$/i, /^nombreCaja$/i, /^descripcioncaja$/i, /^descripcionCaja$/i],
    align: 'left',
    kind: 'text',
  },
  {
    id: 'folio',
    label: 'Folio',
    patterns: [/^folio$/i, /^ticket$/i, /^cuenta$/i, /^nocuenta$/i, /^noCuenta$/i, /^numcuenta$/i],
    align: 'left',
    kind: 'text',
  },
  {
    id: 'mesa',
    label: 'Mesa',
    patterns: [/^mesa$/i, /^numeromesa$/i, /^nromesa$/i, /^noMesa$/i],
    align: 'left',
    kind: 'text',
  },
  {
    id: 'autoriza',
    label: 'Autoriza',
    patterns: [
      /^autoriza$/i,
      /^autorizado$/i,
      /^quienautoriza$/i,
      /^quien.?autoriza$/i,
      /^usuarioautoriza$/i,
      /^usuarioAutoriza$/i,
      /^empleadoautoriza$/i,
      /^empleadoAutoriza$/i,
      /^nombreautoriza$/i,
      /^nombreAutoriza$/i,
      /^capturo$/i,
      /^usuarioqueautoriza$/i,
    ],
    align: 'left',
    kind: 'text',
  },
  {
    id: 'para',
    label: 'Para',
    patterns: [/^para$/i, /^beneficiario$/i, /^destinatario$/i, /^invitado$/i],
    align: 'left',
    kind: 'text',
  },
  {
    id: 'subTotal',
    label: 'SubTotal',
    patterns: [/^subtotal$/i, /^sub_total$/i, /^importeSubtotal$/i],
    align: 'right',
    total: true,
    kind: 'money',
  },
  {
    id: 'pctDesc',
    label: '% Desc',
    patterns: [
      /^porcentajedesc$/i,
      /^porcentajedescuento$/i,
      /^pctdesc$/i,
      /^porcDesc$/i,
      /^%?\s*desc$/i,
      /^tasaDesc$/i,
    ],
    align: 'right',
    kind: 'percent',
  },
  {
    id: 'montoDesc',
    label: '$ Desc',
    patterns: [
      /^descuentopesos$/i,
      /^descuento$/i,
      /^importedescuento$/i,
      /^importeDesc$/i,
      /^montoDesc$/i,
      /^\$\s*desc/i,
      /^valorDescuento$/i,
    ],
    align: 'right',
    total: true,
    kind: 'money',
  },
  {
    id: 'totalMenosDesc',
    label: 'Total - Desc',
    patterns: [/^totalmenosdesc$/i, /^totalMenosDesc$/i, /^totalDesc$/i, /^totalNeto$/i, /^importeNeto$/i],
    align: 'right',
    total: true,
    kind: 'money',
  },
  {
    id: 'cortesia',
    label: 'Cortesia',
    patterns: [/^cortesia$/i, /^cortesía$/i, /^importeCortesia$/i, /^cortesiaImporte$/i],
    align: 'right',
    total: true,
    kind: 'money',
  },
  {
    id: 'pagado',
    label: 'Pagado',
    patterns: [/^pagado$/i, /^importePagado$/i, /^totalPagado$/i, /^montoPagado$/i],
    align: 'right',
    total: true,
    kind: 'money',
  },
];

/**
 * Siempre devuelve las 12 columnas del impreso; `sourceKey` enlaza con el API o `null` (celda vacía).
 * @param {Record<string, unknown>|null|undefined} sampleRow primera fila o null sin datos
 */
export function resolveDescuentosColumns(sampleRow) {
  const keys =
    sampleRow && typeof sampleRow === 'object' && !Array.isArray(sampleRow)
      ? Object.keys(sampleRow)
      : [];
  const used = new Set();
  return DESCUENTOS_COLUMN_SPECS.map((spec) => {
    const k = keys.find((kk) => !used.has(kk) && keyMatches(spec, kk));
    if (k) used.add(k);
    return { ...spec, sourceKey: k ?? null };
  });
}

/** @param {Record<string, unknown>} raw @param {ReturnType<typeof resolveDescuentosColumns>} resolved */
export function rowToDescuentosNorm(raw, resolved) {
  const o = {};
  for (const c of resolved) {
    o[c.id] = c.sourceKey ? raw[c.sourceKey] : undefined;
  }
  return o;
}

/** @param {Record<string, unknown>[]} normRows @param {ReturnType<typeof resolveDescuentosColumns>} resolved */
export function sumDescuentosTotals(normRows, resolved) {
  const totals = {};
  for (const c of resolved) {
    if (!c.total) continue;
    let s = 0;
    for (const r of normRows) {
      const n = Number(r[c.id]);
      if (!Number.isNaN(n)) s += n;
    }
    totals[c.id] = s;
  }
  return totals;
}

const nFinite = (v) => {
  if (v == null || v === '') return null;
  const x = Number(v);
  return Number.isFinite(x) ? x : null;
};

/**
 * `totales` del API (p. ej. subtotal, descuentoPesos) → ids internos de columnas ({@link DESCUENTOS_COLUMN_SPECS}).
 * @param {Record<string, unknown>|null|undefined} apiTotals
 * @returns {Partial<Record<string, number>>|null}
 */
export function mapApiDescuentosTotalesToNormIds(apiTotals) {
  if (!apiTotals || typeof apiTotals !== 'object') return null;
  const out = {};
  const set = (id, ...candidates) => {
    for (const raw of candidates) {
      const x = nFinite(raw);
      if (x != null) {
        out[id] = x;
        return;
      }
    }
  };
  set('subTotal', apiTotals.subtotal, apiTotals.subTotal);
  set('montoDesc', apiTotals.descuentoPesos, apiTotals.descuento_pesos);
  set('totalMenosDesc', apiTotals.totalMenosDesc, apiTotals.total_menos_desc);
  set('cortesia', apiTotals.cortesia);
  set('pagado', apiTotals.pagado);
  return Object.keys(out).length ? out : null;
}

/**
 * Totales de pie: suma en cliente, pisada por valores del API cuando existan.
 * @param {Record<string, number>} summed {@link sumDescuentosTotals}
 * @param {Partial<Record<string, number>>|null|undefined} fromApi {@link mapApiDescuentosTotalesToNormIds}
 * @param {ReturnType<typeof resolveDescuentosColumns>} resolved
 */
export function mergeDescuentosFootTotals(summed, fromApi, resolved) {
  const base = { ...summed };
  if (!fromApi || typeof fromApi !== 'object') return base;
  for (const c of resolved) {
    if (!c.total) continue;
    if (fromApi[c.id] != null && !Number.isNaN(Number(fromApi[c.id]))) {
      base[c.id] = Number(fromApi[c.id]);
    }
  }
  return base;
}

/**
 * Etiquetas de cabecera opcionales desde `columnas` del API (misma clave que la fila).
 * @param {ReturnType<typeof resolveDescuentosColumns>} resolved
 * @param {Record<string, string>|null|undefined} columnas
 */
export function applyDescuentosColumnLabelsFromApi(resolved, columnas) {
  if (!columnas || typeof columnas !== 'object') return resolved;
  return resolved.map((c) => {
    if (!c.sourceKey) return c;
    const label = columnas[c.sourceKey];
    if (typeof label === 'string' && label.trim()) return { ...c, label: label.trim() };
    return c;
  });
}

/**
 * @param {Record<string, unknown>[]} rows
 * @param {ReturnType<typeof resolveDescuentosColumns>} resolved
 * @param {boolean} ordenarMesa
 */
export function sortDescuentosRowsForDisplay(rows, resolved, ordenarMesa) {
  if (!ordenarMesa || !resolved.length) return rows;
  const mesaSpec = resolved.find((c) => c.id === 'mesa');
  if (!mesaSpec?.sourceKey) return rows;
  const sk = mesaSpec.sourceKey;
  return [...rows].sort((a, b) => {
    const va = a[sk];
    const vb = b[sk];
    const na = Number(va);
    const nb = Number(vb);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
    return String(va ?? '').localeCompare(String(vb ?? ''), 'es', { numeric: true });
  });
}

export function formatPeriodoDescuentosLine(fechaInicio, fechaFin) {
  const fmt = (iso) => {
    if (iso == null || iso === '') return '—';
    const s = String(iso);
    const d = new Date(s.length <= 10 ? `${s}T00:00:00` : s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };
  return `Del: ${fmt(fechaInicio)} al ${fmt(fechaFin)}`;
}

/** Montos como en impreso legacy: miles y 2 decimales, sin prefijo $ (mockup). */
function formatNumeroImpreso(n) {
  return n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatDescuentosCell(spec, value) {
  if (value == null || value === '') return '—';
  if (spec.kind === 'money') {
    const n = Number(value);
    return Number.isNaN(n) ? String(value) : formatNumeroImpreso(n);
  }
  if (spec.kind === 'percent') {
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    return `${formatNumeroImpreso(n)}%`;
  }
  if (spec.kind === 'date') {
    const s = String(value);
    const slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slash) {
      const [, d, m, y] = slash;
      return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
    }
    const part = s.split('T')[0];
    const [y, m, d] = part.split('-');
    if (y && m && d) {
      return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
    }
    const d2 = new Date(s.length <= 10 ? `${s}T12:00:00` : s);
    if (!Number.isNaN(d2.getTime())) {
      return d2.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
    return s;
  }
  return String(value);
}

export function formatDescuentosTotalCell(spec, value) {
  if (!spec.total) return '—';
  if (value == null || Number.isNaN(Number(value))) return '—';
  return formatDescuentosCell({ ...spec, kind: 'money' }, value);
}

/**
 * Encabezado unido (mismo criterio que popularidad/mesero), sin subtítulo de cortesías forzado.
 */
export function mergeDescuentosEncabezado(apiEnc, encabezadoFallback) {
  return mergePopularidadEncabezado(apiEnc, encabezadoFallback, {
    reporteTitulo: DESCUENTOS_REPORT_TITULO_DEFAULT,
    incluirCortesias: false,
    lineaCortesias: '',
    cortesiasSubtitle: '',
  });
}

/**
 * Rellena periodo en texto si el API no mandó `periodoTexto`.
 * @param {Record<string, unknown>} enc mergeDescuentosEncabezado(...)
 * @param {string} fechaInicio
 * @param {string} fechaFin
 */
export function withDescuentosPeriodoLine(enc, fechaInicio, fechaFin) {
  const e = enc && typeof enc === 'object' ? { ...enc } : {};
  if (!String(e.periodoTexto || '').trim() && (fechaInicio || fechaFin)) {
    e.periodoTexto = formatPeriodoDescuentosLine(fechaInicio, fechaFin);
  }
  return e;
}
